import { type MessagesType } from './types';

/**
 * English translation resources.
 * Used when the browser language is not French.
 */
const MessagesEN: MessagesType = {
  // Language / dropdown
  'language.label': 'Language',
  'language.fr': 'Français',
  'language.en': 'English',

  // AppBar Console
  'console.title': 'Console',
  'console.auth.openLocked': 'Open Entra sign-in (signed out)',
  'console.auth.openUnlocked': 'Open Entra sign-in (signed in)',
  'console.auth.modalTitle': 'Entra sign-in',
  'console.auth.modalSubtitle':
    'Same flow as Swagger: Microsoft access token (client credentials).',
  'console.auth.modalBody':
    'The token is stored in the browser session and sent as Authorization: Bearer on API calls. The Nest webapp forwards this header only when BFF_FORWARD_CLIENT_AUTHORIZATION=true on the server (see `apps/webapp/.env`).',
  'console.auth.swaggerInfo':
    'The API must run as the local tier (`DEPLOYMENT_ENV=local`, or equivalent) to expose this route (development).',
  'console.auth.tokenActive': 'Token active',
  'console.auth.expires': 'expires {date}',
  'console.auth.clientId': 'client_id (Entra application ID)',
  'console.auth.clientSecret': 'client_secret',
  'console.auth.showSecret': 'Show secret',
  'console.auth.hideSecret': 'Hide secret',
  'console.auth.obtainToken': 'Get token',
  'console.auth.forgetToken': 'Forget token',
  'console.auth.errorMissing': 'Enter client_id and client_secret.',
  'console.auth.errorGeneric':
    'Could not obtain token (check `DEPLOYMENT_ENV=local` on the API and credentials).',
  'console.auth.close': 'Close',
  'appBar.menuUser': 'User menu',

  // Sidebar
  'sidebar.folder': 'Folder',
  'sidebar.users': 'Users',
  'sidebar.settings': 'Settings',

  // Portfolio page
  'page.portfolioTitle': 'Client portfolio',
  'tabs.collect': 'Collection',
  'tabs.equipment': 'Equipment',
  'search.placeholder': 'Search',
  'search.ariaLabel': 'Search',
  'filter.ariaLabel': 'Filter',

  // Table headers
  'table.entreprise': 'COMPANY',
  'table.contact': 'CONTACT',
  'table.receptionFlux': 'FLOW RECEPTION',
  'table.fluxAchat': 'PURCHASE FLOW',
  'table.fluxVente': 'SALES FLOW',
  'table.fluxBanque': 'BANK FLOW',
  'table.paie': 'PAYROLL',
  'table.emailCollecte': 'COLLECTION EMAIL',
  'table.cabinet': 'PRACTICE',

  // Pagination
  'pagination.rowsPerPage': 'Rows per page',
  'pagination.range': '1-10 of 4549',
  'pagination.prev': 'Previous page',
  'pagination.next': 'Next page',

  // Flow reception popover
  'popover.activated': 'Enabled',
  'popover.errorLoadServices':
    'Unable to load the list of integration services.',
  'popover.integrationServiceLabel': 'Integration service',
  'popover.resendEmail': 'Resend activation email',
  'popover.connectService': 'Connect a service',
  'popover.termsPrefix': 'I acknowledge that I have read the',
  'popover.termsLink': 'terms of use',
  'popover.privacyLink': 'privacy policy',
  'popover.termsSuffix': 'to access {serviceName}.',

  // Service descriptions
  'serviceDescription.ouifacture':
    'OuiFacture is an accounting management and production solution for SMEs. Designed to centralise accounting, payroll and management, it integrates with the Cegid ecosystem to feed production tools with reliable, structured and usable data.',
  'serviceDescription.shinePlus':
    'Shine is an invoicing, payments, pre-accounting and business account tool for very small businesses. Designed to simplify data collection and secure financial flows, it integrates with the Cegid ecosystem to feed accounting production tools with reliable, structured and usable data.',

  // Invitation link dialog
  'dialog.invitationTitle': 'Invitation link generated',
  'dialog.invitationDescription':
    'Share this link with the client so they can generate their API keys and complete the service connection.',
  'dialog.copy': 'Copy',

  // Console demo (dev) — matches POST /internal/integrations/invite
  'consoleDemo.selectPartnerProduct': 'Partner product (partnerApplicationId)',
  'consoleDemo.inviteApiHint':
    'Demo: the list comes from GET /internal/integration-partners; each id is a partner_applications.id. The invitation origin is the Entra client_id from the token (Console auth).',
  'consoleDemo.debugOriginEntra': 'Requester Entra app (label + client_id)',
  'consoleDemo.debugProductFromList': 'Product (list label)',
  'consoleDemo.debugPostBodyPrefix': 'POST body (JSON)',

  // Errors
  'error.linkGeneration': 'Error while generating the link.',

  // OuiFacture page (dev)
  'ouiFacture.title': 'OuiFacture',
  'ouiFacture.settingsAriaLabel': 'Open API settings',
  'ouiFacture.uploadCardTitle': 'Sales invoice import',
  'ouiFacture.uploadCardDescription':
    "Upload a sales invoice (BMP, JPG, JPEG, PNG, or PDF) and send it to your accountant's service.",
  'ouiFacture.apiKeyWarning':
    'Please fill in your API key (client ID and client secret) from the settings icon to enable import.',
  'ouiFacture.selectFileLabel': 'Invoice file',
  'ouiFacture.filenameLabel': 'Filename',
  'ouiFacture.userEmailLabel': 'User email',
  'ouiFacture.documentIdLabel': 'Document ID',
  'ouiFacture.sendButton': 'Send',
  'ouiFacture.previewTitle': 'Document preview',
  'ouiFacture.previewFrameTitle': 'Invoice document preview',
  'ouiFacture.previewEmptyState': 'No document uploaded yet.',
  'ouiFacture.settingsDialogTitle': 'OuiFacture API configuration',
  'ouiFacture.settingsDialogDescription':
    'Enter your client ID and client secret to enable the link with your accountant.',
  'ouiFacture.clientIdLabel': 'Client ID',
  'ouiFacture.clientSecretLabel': 'Client secret',
  'ouiFacture.settingsCancel': 'Cancel',
  'ouiFacture.settingsSave': 'Save',
  'ouiFacture.revokeButton': 'Revoke key',
  'ouiFacture.revokeSuccess': 'API key revoked successfully.',
  'ouiFacture.revokeError':
    'Error while revoking the API key (key not found or already revoked).',
  'ouiFacture.success': 'Sales invoice import sent successfully.',
  'ouiFacture.errorMissingFile': 'Please select a file before sending.',
  'ouiFacture.errorInvalidFileType':
    'The file must be BMP, JPG, JPEG, PNG, or PDF.',
  'ouiFacture.errorImportSalesInvoice':
    'Error while importing the sales invoice.',

  // Invitation page (API Key ID + API Secret generation)
  'invitation.pageTitle': 'Connect to your firm',
  'invitation.pageIntro':
    'This page lets you generate an API key to connect your software (billing, ERP, etc.) to your accounting firm. Use the invitation link sent by your advisor to access this feature.',
  'invitation.errorNoTokenTitle': 'Invalid link',
  'invitation.errorNoTokenBody':
    'No invitation link was provided. Use the link sent by your advisor.',
  'invitation.errorInvalidLinkTitle': 'Invalid link',
  'invitation.errorInvalidLinkBody':
    'This invitation link is invalid. Ask your advisor for a new link.',
  'invitation.errorExpiredTitle': 'Link expired',
  'invitation.errorExpiredContactCpa':
    'This link has expired. Ask your advisor for a new link.',
  'invitation.errorExpiredIntegrationOwner':
    'This link has expired. An integration already exists for {firstName} {lastName} ({email}).',
  'invitation.errorRevokedTitle': 'Link revoked',
  'invitation.errorRevokedBody':
    'This link is no longer valid. Ask your advisor for a new link.',
  'invitation.errorRevokedKeyAlreadyActiveTitle': 'API key already created',
  'invitation.errorRevokedKeyAlreadyActiveWithCreator':
    'This link is no longer valid: an API key was already generated for this product by {firstName} {lastName} ({email}). The invitation was deactivated after that.',
  'invitation.errorRevokedKeyAlreadyActiveNoCreator':
    'This link is no longer valid: an API key already exists for this product. The invitation was deactivated. Contact your accountant if you need help.',
  'invitation.revokedIntegrationsTitle': 'Integration identifiers',
  'invitation.revokedIntegrationIdColumn': 'Identifier',
  'invitation.revokedIntegrationStatusColumn': 'Status',
  'invitation.revokedIntegrationStatusActive': 'Active',
  'invitation.revokedIntegrationStatusRevoked': 'Revoked',
  'invitation.revokedCopyId': 'Copy identifier',
  'invitation.revokedRenewSecret': 'Regenerate API key secret',
  'invitation.revokedRenewInProgress': 'Regenerating…',
  'invitation.revokedRevokeKey': 'Revoke API key',
  'invitation.revokedActionsNeedEntra':
    'Entra authentication (advisor tool) is required for these actions.',
  'invitation.revokedRegenerateNeedSelection':
    'Select exactly one integration (active or revoked). A new secret is issued; a revoked row becomes active again.',
  'invitation.revokedRevokeNeedSelection':
    'Select at least one active integration to revoke.',
  'invitation.revokedSelectAllActive': 'Select all active',
  'invitation.revokedSelectRow': 'Select integration {id}',
  'invitation.revokedBulkRevokeSuccess':
    'Revocation: {revoked} revoked, {already} already revoked.',
  'invitation.revokedRegenerateSuccess':
    'New secret generated. Store it securely.',
  'invitation.revokedRevokeSuccess': 'The API key has been revoked.',
  'invitation.revokedActionError': 'Action failed. Check your session.',
  'invitation.revokedNewSecretTitle': 'New API key secret',
  'invitation.revokedDialogClose': 'Close',
  'invitation.retryButton': 'Retry',
  'invitation.errorConnectionTitle': 'Connection error',
  'invitation.verifyingLink': 'Verifying your link…',
  'invitation.sectionTitle': 'API key registration',
  'invitation.secureChip': 'Secure',
  'invitation.cardSubtitle':
    'Your accountant requests access to invoicing data in your {integrationServiceLabel} account.',
  'invitation.contextValidatedSummary':
    'Hello, {businessOwner}, this invoicing data access request was initiated by {collaborator} for the accounting firm {inviterCompanyName}.',
  'invitation.codeExplanation':
    'To generate an API key, first enter the 6-digit code sent by email to {maskedEmail}.',
  'invitation.codeEmailFallback': 'your email address',
  'invitation.validateCodeButton': 'Validate',
  'invitation.validatingCode': 'Validating…',
  'invitation.codeError':
    'Invalid code. Please check the code received by email.',
  'invitation.codeErrorExpired':
    'The code has expired. Use the link above to receive a new code by email.',
  'invitation.codeErrorMaxAttempts':
    'Too many attempts. Use the link above to receive a new code by email.',
  'invitation.codeDigitAria': 'Digit {index} of code',
  'invitation.resendCodeLink': 'Resend code by email',
  'invitation.resendCodeWithCooldown': 'Resend code by email ({seconds} s)',
  'invitation.sendingCode': 'Sending code…',
  'invitation.codeResentSnackbar': 'The code has been resent by email.',
  'invitation.infoInvoicingTitle': 'Invoicing data',
  'invitation.infoInvoicingText':
    'Connect your {integrationServiceLabel} account to share your invoice data.',
  'invitation.infoSecureTitle': 'Private and secure',
  'invitation.infoSecureText':
    "Your password is never requested or transmitted as you're connecting via API.",
  'invitation.infoControlTitle': "You're in control",
  'invitation.infoControlText': 'Withdraw any permissions granted at any time.',
  'invitation.generateButton': 'Generate API key',
  'invitation.generating': 'Generating…',
  'invitation.publicApiIntegrationIdLabel': 'Public API integration ID',
  'invitation.apiIntegrationSecretLabel': 'API key secret',
  'invitation.copyPublicApiIntegrationId': 'Copy integration ID',
  'invitation.copyApiIntegrationSecret': 'Copy secret',
  'invitation.showValue': 'Show',
  'invitation.hideValue': 'Hide',
  'invitation.copiedIdSnackbar': 'Integration ID copied to clipboard',
  'invitation.copiedSecretSnackbar': 'Secret copied to clipboard',
  'invitation.explanatoryText':
    'Use the API Key ID and API Secret in {integrationServiceLabel}, in the section for connections or API settings. Never share the API Secret with third parties or send it by email.',
  'invitation.generateErrorInvalidLink':
    'This link is no longer valid. Unable to generate a key.',
  'invitation.generateErrorSessionExpired':
    'Your session has expired. Please refresh the page.',
};

export default MessagesEN;
